import { Button, useDataProvider } from "react-admin";
import {
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  makeStyles,
  TextField,
} from "@material-ui/core";
import React, { useCallback, useEffect, useState } from "react";
import { StyledButton } from "../../../components/ListShowButton";
import { useField } from "react-final-form";
import { debounce, find, findIndex } from "lodash";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { SchoolIdSelected } from "utils/tools";

const useStyles = makeStyles({
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
      width: "100%",
    },
  },
  studentSearchInput: {
    marginTop: "8px",
    paddingBottom: "3px",
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#EAEAEA",
      },
      "&:hover fieldset": {
        borderColor: "#EAEAEA",
      },
    },
  },
});

interface ManageStudentsButtonProps {
  order: number;
  routeType: string;
  routesData: IRoute[];
}

const ManageStudentsButton: React.FC<ManageStudentsButtonProps> = ({
  order,
  routeType,
  routesData,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [selectedRouteLocation, setSelectedRouteLocation] = useState<any>(null);
  const [selectedStudentIds, setSelectedStudentIds] = useState<number[]>([]);

  const [isHaveDuplicateStudent, setIsHaveDuplicateStudent] =
    useState<boolean>(false);

  const [searchValue, setSearchValue] = useState<string>("");
  const [students, setStudents] = useState<IStudent[]>([]);

  const routeLocationsField = useField("routeLocations");

  const classes = useStyles();
  const provider = useDataProvider();
  const schoolIdSelected = SchoolIdSelected();

  const handleOpenDialogManageStudents = () => {
    const findRouteLocation = find(routeLocationsField.input.value, [
      "order",
      order,
    ]);
    setSelectedRouteLocation(findRouteLocation);
    setSelectedStudentIds(
      findRouteLocation.students.map((student: IStudent) => student.id),
    );
    setOpen(true);
  };

  const handleCloseDialogManageStudents = () => {
    setOpen(false);
  };

  const handleOpenDialogDuplicateStudent = () => {
    setIsHaveDuplicateStudent(true);
  };

  const handleCloseDialogDuplicateStudent = () => {
    setIsHaveDuplicateStudent(false);
  };

  const handleSaveManageStudents = async () => {
    const findRouteLocationIndex = findIndex(routeLocationsField.input.value, [
      "order",
      order,
    ]);

    // get students based on selectedStudentIds with provider.getMany
    const { data: selectedStudents } = await provider.getMany("students", {
      ids: selectedStudentIds,
    });

    selectedRouteLocation.students = selectedStudents;

    routeLocationsField.input.onChange([
      ...routeLocationsField.input.value.slice(0, findRouteLocationIndex),
      selectedRouteLocation,
      ...routeLocationsField.input.value.slice(findRouteLocationIndex + 1),
    ]);

    handleCloseDialogManageStudents();
    handleCloseDialogDuplicateStudent();
  };

  const handleCheckDuplicateStudents = async () => {
    const existingStudentIds = new Set<number>();
    routesData.forEach((route) => {
      route.routeLocations.forEach((loc) => {
        if (loc.id !== selectedRouteLocation.id) {
          if (Array.isArray(loc.students)) {
            loc.students.forEach((student) =>
              existingStudentIds.add(student.id),
            );
          }
        }
      });
    });

    const duplicatedIds = selectedStudentIds.filter((id) =>
      existingStudentIds.has(id),
    );

    if (duplicatedIds.length > 0) {
      handleOpenDialogDuplicateStudent();
    } else {
      await handleSaveManageStudents();
    }
  };

  const getStudents = useCallback(
    debounce((searchString: string) => {
      const filter: any = { SchoolId: schoolIdSelected, Status: "active" };

      if (routeType === "cca") {
        filter.HasCcaSchedule = true;
      }

      if (routeType === "to_school" || routeType === "from_school") {
        filter.HasRegularSchedule = true;
      }

      provider
        .customGetListWithStaticParams(
          `students?filter=${JSON.stringify(
            filter,
          )}&pageSize=50&search=${searchString}`,
        )
        .then((result: any) => {
          setStudents(result.data as IStudent[]);
        });
    }, 1000),
    [routeType],
  );

  useEffect(() => {
    if (searchValue !== "") {
      getStudents(searchValue);
    }
  }, [searchValue, getStudents]);

  return (
    <>
      <StyledButton variant="outlined" onClick={handleOpenDialogManageStudents}>
        Manage Students
      </StyledButton>

      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialog}
        maxWidth="sm"
      >
        <DialogTitle id="alert-dialog-title">
          Change Address of {selectedRouteLocation?.address}
        </DialogTitle>
        <DialogContent>
          <Autocomplete
            multiple
            className={classes.studentSearchInput}
            freeSolo
            disableClearable
            defaultValue={selectedRouteLocation?.students.map(
              (student: IStudent) =>
                `${student.id} - ${student.firstName} ${student.lastName}`,
            )}
            options={students.map((option: IStudent) => {
              return `${option.id} - ${option.firstName} ${option.lastName}`;
            })}
            onChange={(_event, value) => {
              const studentIds = value.map((student: string) => {
                const splitedValue = student.split(" ");
                return Number(splitedValue[0]);
              });

              setSelectedStudentIds(studentIds);
            }}
            onInputChange={(_event, newInputValue) => {
              setSearchValue(newInputValue);
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="outlined"
                  label={option}
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search Student"
                variant="outlined"
                InputProps={{ ...params.InputProps, type: "search" }}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialogManageStudents}
            color="secondary"
            label="Back"
          />
          <Button
            onClick={handleCheckDuplicateStudents}
            color="primary"
            label="Save"
          />
        </DialogActions>
      </Dialog>

      <Dialog
        open={isHaveDuplicateStudent}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="md"
      >
        <DialogTitle id="alert-dialog-title">
          Info Duplicate Student
        </DialogTitle>
        <DialogContent>
          <DialogContentText color="initial">
            Some students that you are trying to add has already been added to
            this wave. do you want to add the students again?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialogDuplicateStudent}
            color="secondary"
            label="Back"
          />
          <Button
            onClick={handleSaveManageStudents}
            color="primary"
            label="Confirm"
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ManageStudentsButton;
